import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { adminService } from '../../services/admin.service';
import { FaPlus, FaTrash, FaKey, FaCopy, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import AdminNavbar from '../../components/admin/AdminNavbar';
import Swal from 'sweetalert2';

const ApiKeysList = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const data = await adminService.getApiKeys();
      setApiKeys(data);
    } catch (error) {
      toast.error('Error al cargar las API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (apiKey) => {
    try {
      await adminService.updateApiKey(apiKey.id, { isActive: !apiKey.isActive });
      toast.success(apiKey.isActive ? 'API key desactivada' : 'API key activada');
      loadApiKeys();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al actualizar la API key');
    }
  };

  const handleDelete = async (apiKey) => {
    const result = await Swal.fire({
      title: '¿Eliminar API key?',
      html: `Se eliminará <strong>${apiKey.name}</strong> (${apiKey.keyPrefix}…). Las integraciones que la usen dejarán de funcionar.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await adminService.deleteApiKey(apiKey.id);
        toast.success('API key eliminada');
        loadApiKeys();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar la API key');
      }
    }
  };

  const copyPrefix = async (prefix) => {
    try {
      await navigator.clipboard.writeText(prefix);
      toast.success('Prefijo copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('es-ES');
  };

  if (loading) {
    return <LoadingMessage>Cargando API keys...</LoadingMessage>;
  }

  return (
    <>
      <AdminNavbar />
      <Container>
        <Header>
          <Title>API Keys</Title>
          <AddButton onClick={() => navigate('/pmpl/admin/api-keys/new')}>
            <FaPlus /> Nueva API Key
          </AddButton>
        </Header>

        <Hint>
          Usa estas claves en el header <code>X-API-Key</code> para{' '}
          <code>POST /api/v1/campaigns</code>. La clave completa solo se muestra al crearla.
        </Hint>

        <KeysGrid>
          {apiKeys.map((apiKey) => (
            <KeyCard key={apiKey.id}>
              <KeyHeader>
                <KeyName>
                  <FaKey /> {apiKey.name}
                </KeyName>
                <StatusBadge active={apiKey.isActive}>
                  {apiKey.isActive ? 'Activa' : 'Inactiva'}
                </StatusBadge>
              </KeyHeader>

              <KeyInfo>
                <InfoItem>
                  <Label>Prefijo</Label>
                  <PrefixRow>
                    <Value mono>{apiKey.keyPrefix}…</Value>
                    <IconButton type="button" onClick={() => copyPrefix(apiKey.keyPrefix)} title="Copiar prefijo">
                      <FaCopy />
                    </IconButton>
                  </PrefixRow>
                </InfoItem>
                <InfoItem>
                  <Label>Creada</Label>
                  <Value>{formatDate(apiKey.createdAt)}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Último uso</Label>
                  <Value>{formatDate(apiKey.lastUsedAt)}</Value>
                </InfoItem>
                {apiKey.createdBy && (
                  <InfoItem>
                    <Label>Creada por</Label>
                    <Value>{apiKey.createdBy}</Value>
                  </InfoItem>
                )}
              </KeyInfo>

              <Actions>
                <ActionButton onClick={() => handleToggle(apiKey)}>
                  {apiKey.isActive ? <FaToggleOn /> : <FaToggleOff />}
                  {apiKey.isActive ? 'Desactivar' : 'Activar'}
                </ActionButton>
                <ActionButton danger onClick={() => handleDelete(apiKey)}>
                  <FaTrash /> Eliminar
                </ActionButton>
              </Actions>
            </KeyCard>
          ))}
        </KeysGrid>

        {apiKeys.length === 0 && (
          <EmptyState>
            <p>No hay API keys todavía.</p>
            <p>Crea una para integrar sistemas externos.</p>
            <AddButton onClick={() => navigate('/pmpl/admin/api-keys/new')}>
              <FaPlus /> Crear primera API key
            </AddButton>
          </EmptyState>
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  color: #ffd700;
  margin: 0;
`;

const Hint = styled.p`
  color: #999;
  margin: 0 0 2rem;
  font-size: 0.9rem;

  code {
    color: #ffd700;
    background: rgba(255, 215, 0, 0.1);
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #ffd700;
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #ffed4a;
  }
`;

const KeysGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const KeyCard = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: 1.5fr 2fr auto;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const KeyHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const KeyName = styled.h3`
  color: #fff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  background: ${(props) => (props.active ? '#34D399' : '#6B7280')};
  color: white;
  width: fit-content;
`;

const KeyInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.span`
  color: #999;
  font-size: 0.8rem;
`;

const Value = styled.span`
  color: #fff;
  font-size: 0.95rem;
  font-family: ${(props) => (props.mono ? 'monospace' : 'inherit')};
`;

const PrefixRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: #ffd700;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;

  &:hover {
    color: #ffed4a;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  background: ${(props) =>
    props.danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 215, 0, 0.1)'};
  color: ${(props) => (props.danger ? '#EF4444' : '#ffd700')};

  &:hover {
    background: ${(props) =>
      props.danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 215, 0, 0.2)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #999;
  background: #2a2a2a;
  border-radius: 8px;

  p {
    margin: 0.5rem 0;
  }

  button {
    margin-top: 1.5rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #fff;
  padding: 2rem;
`;

export default ApiKeysList;
